import React, { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { Users, KeyRound, ShieldAlert, Landmark, Building2, Gavel, BookOpen, Hash, Scale, CalendarOff, Timer, Workflow, Bell, Palette, ScrollText, Activity, Car, BarChart3, Sparkles, Megaphone, RefreshCw, ShieldCheck, DoorOpen, FileSpreadsheet, Route, UserX, Zap, ListChecks, Radio, Send, CalendarDays } from "lucide-react";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import { cls, can, fmtDT, Stat, Badge } from "../../ui";
import { SectionHdr } from "../SummaryTab";
import { RolesTab, UsersTab, HolidaysTab, SettingsTab } from "../../pages/AdminTabs";
import { AuditTab, ConfigTab } from "../../pages/UjmsAdmin";
import { HealthTab } from "../../pages/AdminHealth";
import { NoticesTab } from "../../pages/AdminNotices";
import { LeadsTab } from "../../pages/AdminLeads";
import { DemoJourneyTab } from "../../pages/AdminDemo";
import { CourtroomAssignments } from "./CourtroomAssignments";
import { WorkflowTab } from "../../pages/AdminWorkflow";
import Departments from "../../pages/Departments";
import Reports from "../../pages/Reports";
import TimeStandards from "../standards/TimeStandards";
import TrafficDashboard from "../traffic/TrafficDashboard";
import { CatalogTab, OrgTab, SecurityTab } from "./AdminTools";
import { StatuteCatalog } from "./StatuteCatalog";
import { DataImportExport } from "./DataImportExport";
import { RoutingRules } from "./RoutingRules";
import { ConflictsAdmin } from "./ConflictsAdmin";
import { AutomationRules } from "./AutomationRules";
import { PoliceHub } from "./PoliceHub";
import { DownstreamRouting } from "./DownstreamRouting";
import { PartnerWorkspaces } from "./PartnerWorkspaces";
import { DispositionCodes } from "./DispositionCodes";
import { MotionSpindleAdmin } from "./MotionSpindleAdmin";
import { EditionFrame } from "../EditionFrame";
import { EDITIONS, viewParam } from "../editions";

const ADMIN = ["administration.manage"];
const NAV = [
  { group: "Overview", items: [["overview", "Command overview", Activity, ["administration.manage", "audit.view"]]] },
  { group: "Access & security", items: [["users", "Users", Users, ADMIN], ["roles", "Roles & permissions", KeyRound, ADMIN], ["security", "Sign-in security", ShieldAlert, ADMIN]] },
  { group: "Organization", items: [["org", "Court structure", Landmark, ADMIN], ["courtrooms", "Courtroom assignments", DoorOpen, ADMIN], ["departments", "Departments", Building2, ADMIN], ["rules", "Assignment & numbering", Gavel, ADMIN]] },
  { group: "Configuration", items: [["catalog", "Catalogs & templates", BookOpen, ADMIN], ["statutes", "Statutory authority", Scale, ADMIN], ["standards", "Time standards", Timer, ADMIN], ["calendar", "Court calendar", CalendarOff, ADMIN], ["workflow", "Workflow engine", Workflow, ADMIN], ["notices", "Notices", Bell, ADMIN], ["settings", "Branding & settings", Palette, ADMIN]] },
  { group: "Rules & automation", items: [["routing", "Routing rules engine", Route, ADMIN], ["conflicts", "Judicial conflicts", UserX, ADMIN], ["automation", "Automation rules", Zap, ADMIN], ["spindle", "Motion spindle (motion call)", CalendarDays, ADMIN], ["dispcodes", "Disposition codes", ListChecks, ADMIN]] },
  { group: "Justice partners", items: [["hub", "Police & prosecutor API hub", Radio, ADMIN], ["downstream", "Downstream routing (CHRI · CCDOC · NICS)", Send, ["administration.manage", "audit.view", "custody.manage"]], ["partners", "Appellate Court & ISP workspaces", Scale, ADMIN]] },
  { group: "Data", items: [["data", "Import & export (Excel)", FileSpreadsheet, ["administration.manage", "case.create"]]] },
  { group: "Oversight", items: [["audit", "Audit log", ScrollText, ["audit.view", "administration.manage"]], ["health", "System health", Activity, ADMIN], ["traffic", "Traffic dashboard", Car, ["audit.view", "finance.disburse", "administration.manage"]], ["reports", "Reports", BarChart3, ["report.view", "administration.manage"]]] },
  { group: "Platform", items: [["demo", "Demo journey", Sparkles, ADMIN], ["leads", "Sales leads & workspaces", Megaphone, ADMIN]] },
];
export const ADMIN_SECTIONS = NAV.flatMap((g) => g.items.map((i) => i[0]));

export default function AdminEdition() {
  const { access } = useOutletContext();
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const section = viewParam(sp) || "overview";
  const isTenant = Boolean(user?.tenant);
  const visible = NAV.filter((g) => !(isTenant && g.group === "Platform")).map((g) => ({ ...g, items: g.items.filter(([, , , perms]) => can(access, ...perms)) })).filter((g) => g.items.length);
  const allowed = visible.flatMap((g) => g.items.map((i) => i[0]));
  const current = allowed.includes(section) ? section : allowed[0];
  const setSection = (s) => setSp({ view: s });
  const ed = EDITIONS.find((e) => e.key === "admin");
  const currentView = { key: current, label: NAV.flatMap((g) => g.items).find((i) => i[0] === current)?.[1] };
  if (!allowed.length) return <div className="ent-panel p-4 text-[12px] text-slate-600" data-testid="admin-restricted"><b>Administration is restricted.</b> Your role does not include administrative permissions.</div>;
  return (
    <EditionFrame edition={ed} views={[currentView]} view={current} setView={setSection} subtitle="Access, organization, configuration and oversight for the whole court" testId="admin-edition">
      <div className="grid grid-cols-[190px_minmax(0,1fr)] gap-1.5 flex-1 min-h-0">
        <nav className="ent-panel self-start" data-testid="admin-nav">
          {visible.map((g) => (
            <div key={g.group}>
              <div className="px-2 pt-1.5 pb-px text-[9.5px] font-bold uppercase tracking-wider text-slate-500">{g.group}</div>
              {g.items.map(([key, label, Icon]) => (
                <button key={key} onClick={() => setSection(key)} data-testid={`admin-nav-${key}`} aria-current={current === key}
                  className={cls("w-full flex items-center gap-1.5 px-2 py-[3px] text-[11px] text-left border-l-2", current === key ? "border-ujms-dark bg-ujms-soft text-ujms-deep font-semibold" : "border-transparent text-slate-700 hover:bg-slate-100")}>
                  <Icon className="w-3 h-3 shrink-0" /> {label}
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div className="min-w-0 ent-legacy" data-testid={`admin-section-${current}`}>
          {current === "overview" && <Overview setSection={setSection} />}
          {current === "users" && <UsersTab />}
          {current === "roles" && <RolesTab />}
          {current === "security" && <SecurityTab />}
          {current === "org" && <OrgTab />}
          {current === "courtrooms" && <CourtroomAssignments />}
          {current === "departments" && <Departments />}
          {current === "rules" && <ConfigTab />}
          {current === "catalog" && <CatalogTab />}
          {current === "statutes" && <StatuteCatalog />}
          {current === "standards" && <TimeStandards />}
          {current === "calendar" && <HolidaysTab />}
          {current === "workflow" && <WorkflowTab />}
          {current === "notices" && <NoticesTab />}
          {current === "settings" && <SettingsTab />}
          {current === "audit" && <AuditTab />}
          {current === "health" && <HealthTab />}
          {current === "traffic" && <TrafficDashboard />}
          {current === "reports" && <Reports />}
          {current === "demo" && <DemoJourneyTab />}
          {current === "leads" && <LeadsTab />}
          {current === "data" && <DataImportExport />}
          {current === "routing" && <RoutingRules />}
          {current === "conflicts" && <ConflictsAdmin />}
          {current === "automation" && <AutomationRules />}
          {current === "dispcodes" && <DispositionCodes />}
          {current === "spindle" && <MotionSpindleAdmin />}
          {current === "hub" && <PoliceHub />}
          {current === "downstream" && <DownstreamRouting />}
          {current === "partners" && <PartnerWorkspaces />}
        </div>
      </div>
    </EditionFrame>
  );
}

function Overview({ setSection }) {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const load = () => api.get("/ujms/v2/admin/overview").then((r) => setD(r.data)).catch(() => setD({ kpis: {}, recent_audit: [] }));
  useEffect(() => { load(); }, []);
  const k = d?.kpis || {};
  return (
    <div className="space-y-1.5" data-testid="admin-overview">
      <div className="grid grid-cols-4 md:grid-cols-6 gap-1" data-testid="admin-kpis">
        <Stat label="Users" value={k.users} onClick={() => setSection("users")} testId="ak-users" />
        <Stat label="Locked accounts" value={k.locked_accounts} tone={k.locked_accounts ? "text-red-700" : "text-emerald-700"} onClick={() => setSection("security")} testId="ak-locked" />
        <Stat label="Roles" value={k.roles} onClick={() => setSection("roles")} testId="ak-roles" />
        <Stat label="Permissions" value={k.permissions} onClick={() => setSection("roles")} testId="ak-perms" />
        <Stat label="Audit events today" value={k.audit_today} onClick={() => setSection("audit")} testId="ak-audit" />
        <Stat label="Cases opened today" value={k.cases_today} onClick={() => nav("/ujms/cases")} testId="ak-cases" />
        <Stat label="Filings awaiting review" value={k.filings_pending} tone="text-amber-700" onClick={() => nav("/ujms/filings")} testId="ak-filings" />
        <Stat label="Open notices" value={k.notices_active} onClick={() => setSection("notices")} testId="ak-notices" />
        <Stat label="Upcoming closures" value={k.holidays_upcoming} onClick={() => setSection("calendar")} testId="ak-holidays" />
        <Stat label="Workflows needing attention" value={k.workflow_attention} tone={k.workflow_attention ? "text-red-700" : undefined} onClick={() => setSection("workflow")} testId="ak-workflows" />
        <Stat label="Inactive users" value={k.users_inactive} onClick={() => setSection("users")} testId="ak-inactive" />
        <Stat label="Sign-ins (7 days)" value={k.logins_week} testId="ak-logins" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-[3fr_2fr] gap-1.5">
        <section className="ent-panel" data-testid="admin-recent-audit">
          <SectionHdr title="Latest administrative & system events" right={<button className="underline" onClick={load} data-testid="admin-overview-refresh"><RefreshCw className="w-3 h-3 inline" /></button>} />
          <table className="w-full border-collapse text-[11px]">
            <tbody>
              {(d?.recent_audit || []).map((e, i) => <tr key={e.id || i} className="border-b border-slate-100 hover:bg-ujms-soft/60"><td className="px-1.5 py-[2px] font-mono whitespace-nowrap">{fmtDT(e.timestamp)}</td><td className="px-1.5"><Badge value={e.action} /></td><td className="px-1.5">{e.actor_name || e.actor}</td><td className="px-1.5 text-slate-500 truncate max-w-[280px]">{e.resource}{e.resource_id ? ` · ${String(e.resource_id).slice(0, 12)}` : ""}</td></tr>)}
              {d && !d.recent_audit?.length && <tr><td className="px-2 py-1 italic text-slate-400">No events yet.</td></tr>}
            </tbody>
          </table>
        </section>
        <section className="ent-panel" data-testid="admin-quicklinks">
          <SectionHdr title="Quick actions" />
          <div className="p-1.5 grid grid-cols-2 gap-1 text-[11px]">
            {[["users", "Provision a user"], ["roles", "Edit a role's permissions"], ["security", "Unlock a sign-in"], ["org", "Add a courtroom or judge"], ["courtrooms", "Seat a judge in a courtroom"], ["catalog", "Add a hearing or filing type"], ["statutes", "Add or edit a statute / charge"], ["rules", "Change case numbering"], ["calendar", "Add a court closure"], ["standards", "Tune case-age standards"], ["workflow", "Inspect workflow runs"], ["settings", "Update branding"]].map(([s, l]) => (
              <button key={s} className="text-left border border-slate-300 bg-white px-2 py-1 rounded-sm hover:bg-ujms-soft hover:border-ujms" onClick={() => setSection(s)} data-testid={`admin-quick-${s}`}>{l}</button>
            ))}
            <Link to="/dashboard" className="text-left border border-slate-300 bg-white px-2 py-1 rounded-sm hover:bg-ujms-soft hover:border-ujms" data-testid="admin-quick-dashboard">Open my dashboard</Link>
            <Link to="/officer" className="text-left border border-slate-300 bg-white px-2 py-1 rounded-sm hover:bg-ujms-soft hover:border-ujms" data-testid="admin-quick-officer">Law enforcement (agency view)</Link>
          </div>
        </section>
      </div>
    </div>
  );
}
